//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import HarvestLogModel from './harvestLog.model';
import FarmSiteModel from './farmSite.model';

/**
 * @service HarvestLogService
 * @description Service for managing harvest log data and related aquaculture metrics.
 * This service provides HTTP methods to retrieve harvest activities, harvest DOC (Days of Culture) summaries,
 * survival rate summaries, and detailed metrics for farm sites.
 * It interacts with the `/blubay/harvestlog/` backend endpoint.
 * Extends the base Service class with the generic type HarvestLogModel.
 *
 * @feature 1. Fetch all harvest log activities with pagination.
 * @feature 2. Retrieve today's harvest counts for specific farm sites.
 * @feature 3. Get aggregated harvest DOC summaries and location-based data.
 * @feature 4. Get aggregated survival rate summaries and location-based data.
 * @feature 5. Fetch detailed metrics for survival rates and harvested DOC.
 * @feature 6. Cancel ongoing HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends the base `Service<HarvestLogModel>` to provide standardized REST API
 * integration. It uses Angular's `HttpClient` for all HTTP operations and manages
 * subscriptions in an `ongoingRequests` array for easy cancellation, ensuring
 * robust resource management.
 *
 * @service-endpoint /blubay/harvestlog/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class HarvestLogService extends Service<HarvestLogModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * This is used to manage and cancel pending requests, for example, when a component is destroyed.
   * @default []
   * @side-effect Stores subscription references, which are later used by `cancelOnGoingHttpRequests()`.
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor that injects the HttpClient and sets up the service endpoint.
   *
   * Calls the parent `Service` constructor, passing the `HttpClient` instance and the
   * specific API endpoint path for harvest log operations.
   *
   * @param {HttpClient} httpClient - @service Angular's HTTP client for making API requests.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/harvestlog/');
  }

  /**
   * @method getAllHarvestLogActivities
   * @async
   * @description Fetches all harvest log activities from the backend with pagination.
   *
   * Makes an HTTP GET request to retrieve a list of all harvest log records, ordered by the newest first.
   *
   * @returns {Promise<Array<HarvestLogModel>>} A promise that resolves with an array of harvest log models.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP GET request and adds its subscription to the `ongoingRequests` array.
   *
   * @example
   * const activities = await harvestLogService.getAllHarvestLogActivities();
   * console.log('All harvest activities:', activities);
   */
  public getAllHarvestLogActivities() {
    return new Promise<Array<HarvestLogModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<HarvestLogModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAlltodayHarvest
   * @async
   * @description Fetches the count of harvest logs recorded today for a list of farm sites.
   *
   * @param {FarmSiteModel[]} farmSiteIds - An array of farm site models.
   * @returns {Promise<Array<HarvestLogModel>>} A promise that resolves with an array containing harvest count data.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP POST request.
   */
  public getAlltodayHarvest(farmSiteIds: FarmSiteModel[]) {
    return new Promise<Array<HarvestLogModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<HarvestLogModel>>(`${this.baseUrl}${this.serviceUrl}count/farmSite`, {
        farmSite: farmSiteIds
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getHarvestDOCSummary
   * @async
   * @description Fetches a summary of Harvested Days of Culture (DOC) for a list of farm sites.
   *
   * @param {FarmSiteModel[]} farmSites - An array of farm site models.
   * @returns {Promise<Array<HarvestLogModel>>} A promise that resolves with an array of harvest DOC summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP POST request.
   */
  public getHarvestDOCSummary(farmSites: FarmSiteModel[]) {
    return new Promise<Array<HarvestLogModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<HarvestLogModel>>(`${this.baseUrl}${this.serviceUrl}harvestedDoc/summary/farmSite`, {
        farmSite: farmSites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSurvivalRateSummary
   * @async
   * @description Fetches a summary of survival rates for a list of farm sites.
   * Note: This endpoint seems to be under `/blubay/feedlog/` path in the implementation, which might be shared logic or a specific endpoint for survival rates derived from feed/harvest data.
   *
   * @param {FarmSiteModel[]} farmSites - An array of farm site models.
   * @returns {Promise<Array<HarvestLogModel>>} A promise that resolves with an array of survival rate summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP POST request.
   */
  public getSurvivalRateSummary(farmSites: FarmSiteModel[]) {
    return new Promise<Array<HarvestLogModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<HarvestLogModel>>(`${this.baseUrl}/blubay/feedlog/survivalRate/summary/farmSite`, {
        farmSite: farmSites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getHarvestDOCLocation
   * @async
   * @description Fetches harvest DOC data grouped by location for a list of farm sites.
   *
   * @param {FarmSiteModel[]} farmSites - An array of farm site models.
   * @returns {Promise<Array<HarvestLogModel>>} A promise that resolves with an array of location-based harvest DOC data.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP POST request.
   */
  public getHarvestDOCLocation(farmSites: FarmSiteModel[]) {
    return new Promise<Array<HarvestLogModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<HarvestLogModel>>(`${this.baseUrl}${this.serviceUrl}harvestedDoc/location/farmSite`, {
        farmSite: farmSites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSurvivalRateLocation
   * @async
   * @description Fetches survival rate data grouped by location for a list of farm sites.
   * Note: Uses `/blubay/feedlog/` base path.
   *
   * @param {FarmSiteModel[]} farmSites - An array of farm site models.
   * @returns {Promise<Array<HarvestLogModel>>} A promise that resolves with an array of location-based survival rate data.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP POST request.
   */
  public getSurvivalRateLocation(farmSites: FarmSiteModel[]) {
    return new Promise<Array<HarvestLogModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<HarvestLogModel>>(`${this.baseUrl}/blubay/feedlog/survivalRate/location/farmSite`, {
        farmSite: farmSites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSurvivalRateDetails
   * @async
   * @description Fetches detailed survival rate metrics, including pond and section names, for a list of farm sites.
   * Note: Uses `/blubay/feedlog/` base path.
   *
   * @param {FarmSiteModel[]} farmSites - An array of farm site models.
   * @returns {Promise<Array<HarvestLogModel>>} A promise that resolves with an array of detailed survival rate data.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP POST request.
   */
  public getSurvivalRateDetails(farmSites: FarmSiteModel[]) {
    return new Promise<Array<HarvestLogModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<HarvestLogModel>>(`${this.baseUrl}/blubay/feedlog/pondName/sectionName/survivalRate/summary/farmSite`, {
        farmSite: farmSites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getHarvestedDOCDetails
   * @async
   * @description Fetches detailed metrics for Harvested Days of Culture (DOC) for a list of farm sites.
   *
   * @param {FarmSiteModel[]} farmSites - An array of farm site models.
   * @returns {Promise<Array<HarvestLogModel>>} A promise that resolves with an array of detailed harvest DOC metrics.
   * @throws {HttpErrorResponse} Rejects the promise if the API call fails.
   * @side-effect Creates an HTTP POST request.
   */
  public getHarvestedDOCDetails(farmSites: FarmSiteModel[]) {
    return new Promise<Array<HarvestLogModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<HarvestLogModel>>(`${this.baseUrl}${this.serviceUrl}harvestedDoc/metrics/summary/farmSite`, {
        farmSite: farmSites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

   //To Cancel the onGoing Http Requests
   /**
   * @method cancelOnGoingHttpRequests
   * @description Cancels all ongoing/pending HTTP requests initiated by this service.
   *
   * Iterates through the `ongoingRequests` array and unsubscribes from each active
   * subscription. This is a crucial cleanup step to prevent memory leaks and
   * race conditions when a component is destroyed.
   *
   * @execution-flow
   * 1. Iterate through each subscription in the `ongoingRequests` array.
   * 2. Check if the subscription object is valid and has an `unsubscribe` function.
   * 3. Call `unsubscribe()` on each active subscription to abort the HTTP request.
   * 4. Clear the `ongoingRequests` array.
   *
   * @returns {void}
   * @side-effect Aborts all pending HTTP requests and clears the `ongoingRequests` array.
   * @usage This method should be called in the `ngOnDestroy` lifecycle hook of any
   * component that uses this service to ensure proper cleanup.
   */
   public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
